import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import Link from 'next/link'
import gql from 'graphql-tag'
import initApollo from '@lib/apollo-init'
import {
  formatCoachHitDistanceLabel,
  getCoachHitFacility,
  getCoachHitPlace,
} from '@lib/algolia/coach-geoloc'
import Card from '@material-ui/core/Card'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Dialog from '@material-ui/core/Dialog'
import DialogContent from '@material-ui/core/DialogContent'
import CircularProgress from '@material-ui/core/CircularProgress'
import { IconClose } from '@components/atoms/icons'
import PropTypes from 'prop-types'
import Image from '@components/atoms/image'
import Embed from '@components/embed/embed'
import { extractYouTubeVideoId } from '@lib/pga-coach/youtube'
import { gtmEvent } from '@lib/gtm-utils'
import * as amplitude from '@amplitude/analytics-browser'
import { useRouter } from 'next/router'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import PlayArrowRoundedIcon from '@material-ui/icons/PlayArrowRounded'
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder'
import FavoriteIcon from '@material-ui/icons/Favorite'
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined'
import { Zap } from 'lucide-react'
import NoSsr from '@material-ui/core/NoSsr'
import useFeatureFlags from '@lib/feature-flags/useFeatureFlags'
import { flags } from '@lib/feature-flags/constants'
import {
  getCoachMembershipDisplayAccreditation,
} from '@lib/pga-coach/coach-credentials'
import CoachAccreditationExplainerDialog, {
  getAccreditationExplainer,
} from '@components/coach-accreditation-explainer-dialog'
import initials from '@utils/initials'
import stripMarkdown from '@lib/strip-markdown'
import { composeOptimizedImageUrl } from '@utils/composeOptimizedImageUrl'
import { BOOK_THIS_WEEK_LABEL } from '@lib/coaches-directory/book-this-week'
import MembershipTag from './membership-tag'
import useStyles from './coach-card.styles'

// Enough plaintext to fill two lines on a 3-across card; CSS line-clamp
// draws the ellipsis. Cap the DOM payload so a 5k-char Algolia bio is not
// copied into every hit.
export const BIO_SNIPPET_MAX_CHARS = 240
const PHOTO_WIDTH = 640

const COACH_INTRO_VIDEO_QUERY = gql`
  query CoachIntroVideoUrl($customSlug: String!) {
    CoachProfiles(customSlug: $customSlug) {
      profiles {
        introductionVideoUrl
      }
    }
  }
`

function coachHitHasIntroVideo(hasIntroVideo, introductionVideoUrl) {
  if (introductionVideoUrl) return true
  if (hasIntroVideo === true || hasIntroVideo === 1) return true
  if (typeof hasIntroVideo === 'string' && hasIntroVideo.toLowerCase() === 'true') return true
  return false
}

function normalizeCoachCardName(value) {
  return (value || '').replace(/,\s*PGA\s*$/i, '').replace(/\s+/g, ' ').trim()
}

export function formatCoachCardName({ firstName, lastName, displayName } = {}) {
  const display = normalizeCoachCardName(displayName)
  if (display) return display
  return [(firstName || '').trim(), (lastName || '').trim()].filter(Boolean).join(' ')
}

function formatFacilityName(facility) {
  return facility?.facilityName || facility?.name || null
}

export function formatCoachCardBioSnippet(bio) {
  const text = stripMarkdown(bio || '').replace(/\s+/g, ' ').trim()
  if (!text) return null
  if (text.length <= BIO_SNIPPET_MAX_CHARS) return text
  const sliced = text.slice(0, BIO_SNIPPET_MAX_CHARS)
  const lastSpace = sliced.lastIndexOf(' ')
  return (lastSpace > 40 ? sliced.slice(0, lastSpace) : sliced).trim()
}

/** Format Algolia `coachProfiles.startingPrice` (cents, e.g. 9000 → From $90). */
export function formatCoachCardStartingPrice(startingPrice) {
  if (startingPrice == null || startingPrice === '') return null
  const cents = Number(startingPrice)
  if (!Number.isFinite(cents) || cents <= 0) return null
  const dollars = cents / 100
  const formatted = Number.isInteger(dollars)
    ? `$${dollars.toLocaleString('en-US')}`
    : `$${dollars.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  return `From ${formatted}`
}

function CoachCard({
  coachInfo,
  isStory = false,
  source,
  isSaved = false,
  onToggleSaved,
  hideSaveToggle = false,
  bookThisWeek = false,
  layout = 'grid',
  externalFacilityId,
  title,
}) {
  const {
    profilePhoto,
    displayName,
    city,
    state,
    customSlug,
    firstName,
    lastName,
    facilities,
    bookable,
    hasIntroVideo,
    introductionVideoUrl,
    objectID,
    universalId,
    memberType,
    certificationLevel,
    hasPlusSubscription,
    randomInt,
    _rankingInfo,
    acceptsLeads,
    bio,
    startingPrice,
    __position,
  } = coachInfo

  const [isInfoVisualizationEnabled] = useFeatureFlags([
    flags.FLAG_COACH_SEARCH_INFO_VISUALIZATION,
  ])

  const styles = useStyles()
  const router = useRouter()
  const isEmbeddedLayout = layout === 'embedded'

  const [isIntroVideoOpen, setIsIntroVideoOpen] = useState(false)
  const [openAccreditationId, setOpenAccreditationId] = useState(null)
  const [resolvedIntroductionVideoUrl, setResolvedIntroductionVideoUrl] = useState(null)
  const [isFetchingIntroVideo, setIsFetchingIntroVideo] = useState(false)
  const [introVideoFetchFailed, setIntroVideoFetchFailed] = useState(false)
  const introVideoFetchInFlight = useRef(false)
  const hasPrefetchedIntroVideo = useRef(false)

  const membershipDisplayAccreditation = useMemo(
    () => getCoachMembershipDisplayAccreditation({ memberType, certificationLevel }),
    [memberType, certificationLevel],
  )
  const membershipExplainerId = membershipDisplayAccreditation
    && getAccreditationExplainer(membershipDisplayAccreditation.id)
    ? membershipDisplayAccreditation.id
    : null

  const coachName = formatCoachCardName({
    firstName,
    lastName,
    displayName,
  })
  const profileHref = customSlug ? `/coach/${customSlug}` : null
  const facilityOptions = { externalFacilityId }
  const matchedFacility = getCoachHitFacility({ facilities, _rankingInfo }, facilityOptions)
  const facilityName = formatFacilityName(matchedFacility)
  const placeLabel = getCoachHitPlace({
    facilities, city, state, _rankingInfo,
  }, facilityOptions)
  const distanceLabel = formatCoachHitDistanceLabel(
    _rankingInfo?.matchedGeoLocation?.distance,
  )
  const bioSnippet = formatCoachCardBioSnippet(bio)
  const startingPriceLabel = formatCoachCardStartingPrice(startingPrice)
  const profileAriaLabel = bookThisWeek
    ? `View profile for ${coachName}, available this week`
    : `View profile for ${coachName}`
  const showIntroVideoBadge = coachHitHasIntroVideo(hasIntroVideo, introductionVideoUrl)
  const effectiveIntroductionVideoUrl = resolvedIntroductionVideoUrl || introductionVideoUrl
  const videoId = effectiveIntroductionVideoUrl
    ? extractYouTubeVideoId(effectiveIntroductionVideoUrl)
    : null
  const coachInitials = initials(firstName, lastName)
  const photoSrc = profilePhoto ? composeOptimizedImageUrl(profilePhoto, PHOTO_WIDTH) : null

  const fetchIntroVideoUrl = useCallback(async () => {
    if (!customSlug || introVideoFetchInFlight.current) {
      return videoId ? effectiveIntroductionVideoUrl : null
    }
    if (videoId) {
      return effectiveIntroductionVideoUrl
    }

    introVideoFetchInFlight.current = true
    setIsFetchingIntroVideo(true)
    setIntroVideoFetchFailed(false)
    try {
      const { data } = await initApollo().query({
        query: COACH_INTRO_VIDEO_QUERY,
        variables: { customSlug },
        context: { clientName: 'coach-tools' },
        fetchPolicy: 'network-only',
      })
      const fetchedUrl = data?.CoachProfiles?.profiles?.[0]?.introductionVideoUrl
      if (fetchedUrl && extractYouTubeVideoId(fetchedUrl)) {
        setResolvedIntroductionVideoUrl(fetchedUrl)
        return fetchedUrl
      }
      setIntroVideoFetchFailed(true)
    } catch (error) {
      setIntroVideoFetchFailed(true)
    } finally {
      introVideoFetchInFlight.current = false
      setIsFetchingIntroVideo(false)
    }
    return null
  }, [
    customSlug,
    effectiveIntroductionVideoUrl,
    videoId,
  ])

  useEffect(() => {
    if (!showIntroVideoBadge || videoId || !customSlug || hasPrefetchedIntroVideo.current) {
      return undefined
    }
    hasPrefetchedIntroVideo.current = true
    fetchIntroVideoUrl()
    return undefined
  }, [showIntroVideoBadge, videoId, customSlug, fetchIntroVideoUrl])

  const handleIntroVideoBadgeClick = async (event) => {
    event.preventDefault()
    event.stopPropagation()
    amplitude.track('play-video', {
      content_name: `Coach intro video - ${coachName}`,
      status: 'start',
      source,
      page_path: router?.asPath,
      coach_profile_id: objectID,
      video_url: effectiveIntroductionVideoUrl || null,
    })
    setIsIntroVideoOpen(true)
    if (!videoId) {
      await fetchIntroVideoUrl()
    }
  }

  const handleIntroVideoDialogClose = () => {
    setIsIntroVideoOpen(false)
  }

  const handleMembershipExplainerClose = useCallback(() => {
    setOpenAccreditationId(null)
  }, [])

  // Grid layout overlays these on the photo; embedded layout shows them atop the content column.
  const coachIdentity = (
    <>
      {coachName && (
        <Typography
          className={isEmbeddedLayout
            ? `${styles.coachName} ${styles.coachNameEmbedded}`
            : styles.coachName}
          variant="h5"
          variantMapping={{ h5: 'h3' }}
          data-testid="coach-card-name"
        >
          {coachName}
        </Typography>
      )}
      {title && (
        <Typography
          className={isEmbeddedLayout
            ? `${styles.photoFacility} ${styles.photoFacilityEmbedded}`
            : styles.photoFacility}
          variant="body2"
          data-testid="coach-card-title"
        >
          {title}
        </Typography>
      )}
      {facilityName && (
        <Typography
          className={isEmbeddedLayout
            ? `${styles.photoFacility} ${styles.photoFacilityEmbedded}`
            : styles.photoFacility}
          variant="body2"
          data-testid="coach-card-location"
          title={facilityName}
        >
          {facilityName}
        </Typography>
      )}
      {placeLabel && (
        <Typography
          className={isEmbeddedLayout
            ? `${styles.photoPlace} ${styles.photoPlaceEmbedded}`
            : styles.photoPlace}
          variant="body2"
          data-testid="coach-card-place"
        >
          {placeLabel}
        </Typography>
      )}
    </>
  )

  return (
    <Card
      className={[
        styles.root,
        isEmbeddedLayout ? styles.rootEmbeddedOverride : '',
        profileHref ? '' : styles.rootStatic,
      ].filter(Boolean).join(' ')}
      onClick={profileHref ? () => gtmEvent({
        event: 'click-coach-card',
        attributes: {
          bookable,
          book_this_week: bookThisWeek,
          coach_name: coachName,
          coach_profile_id: objectID,
          has_video: hasIntroVideo,
          source,
          has_plus_subscription: hasPlusSubscription,
          member_type: memberType,
          certification_level: certificationLevel,
          accepts_leads: acceptsLeads,
          actionable: acceptsLeads || bookable,
          list_position: __position,
        },
      }) : undefined}
    >
      <Box
        className={isEmbeddedLayout
          ? `${styles.photoStage} ${styles.photoStageEmbeddedOverride}`
          : styles.photoStage}
        data-testid="coach-card-photo"
      >
        {!isStory && !hideSaveToggle && typeof onToggleSaved === 'function' ? (
          <Box className={styles.saveDock}>
            <NoSsr>
              <Tooltip title={isSaved ? 'Remove from saved coaches' : 'Save coach'}>
                <IconButton
                  type="button"
                  size="small"
                  aria-label={isSaved ? 'Remove coach from saved list' : 'Save coach to your list'}
                  aria-pressed={Boolean(isSaved)}
                  className={`${styles.saveToggle} ${isSaved ? styles.saveToggleActive : ''}`}
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    onToggleSaved(objectID)
                  }}
                >
                  {isSaved
                    ? <FavoriteIcon fontSize="small" />
                    : <FavoriteBorderIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </NoSsr>
          </Box>
        ) : null}

        {photoSrc ? (
          <Image
            data-testid="coachImage"
            className={styles.coachImage}
            src={photoSrc}
            alt=""
          />
        ) : (
          <Box className={styles.coachImage} aria-hidden>
            <span className={styles.coachInitials}>{coachInitials}</span>
          </Box>
        )}

        {!isEmbeddedLayout && <Box className={styles.photoScrim} aria-hidden />}
        {showIntroVideoBadge && (
          <button
            type="button"
            className={styles.videoControl}
            data-testid="coach-card-video-badge"
            aria-label={`Watch intro video for ${coachName}`}
            data-gtm-element-name="coach-intro-video-open"
            data-gtm-coach-id={universalId}
            data-gtm-coach-profile-id={objectID}
            onClick={handleIntroVideoBadgeClick}
          >
            <span className={styles.videoBadge}>
              <PlayArrowRoundedIcon className={styles.videoBadgeIcon} aria-hidden />
            </span>
            <span className={styles.watchIntroLabel}>Watch Intro</span>
          </button>
        )}
        {!isEmbeddedLayout && (
          <Box
            className={showIntroVideoBadge
              ? `${styles.photoCaption} ${styles.photoCaptionWithVideo}`
              : styles.photoCaption}
            data-testid="coach-card-photo-caption"
          >
            {coachIdentity}
          </Box>
        )}
      </Box>

      <Box
        className={isEmbeddedLayout
          ? `${styles.contentContainer} ${styles.contentContainerEmbeddedOverride}`
          : styles.contentContainer}
      >
        {isEmbeddedLayout && (coachName || facilityName || placeLabel) && (
          <Box
            className={styles.embeddedIdentity}
            data-testid="coach-card-identity"
          >
            {coachIdentity}
          </Box>
        )}
        {(membershipDisplayAccreditation?.label || startingPriceLabel || distanceLabel
            || bookThisWeek) && (
          <Box className={styles.contentHeader}>
            {(startingPriceLabel || distanceLabel || bookThisWeek) && (
              <Box className={styles.statsBlock} data-testid="coach-card-stats">
                {startingPriceLabel && (
                  <Typography
                    className={styles.priceText}
                    variant="body2"
                    data-testid="coach-card-price"
                  >
                    {startingPriceLabel}
                  </Typography>
                )}
                {distanceLabel && (
                  <Typography
                    className={styles.distanceText}
                    variant="body2"
                    component="p"
                    data-testid="coach-card-distance"
                  >
                    <RoomOutlinedIcon className={styles.distanceIcon} aria-hidden />
                    {distanceLabel}
                  </Typography>
                )}
                {bookThisWeek && (
                  <Typography
                    className={styles.distanceText}
                    variant="body2"
                    component="p"
                    data-testid="coach-card-book-this-week"
                  >
                    <Zap className={styles.bookThisWeekIcon} size={18} strokeWidth={1.5} aria-hidden />
                    {BOOK_THIS_WEEK_LABEL}
                  </Typography>
                )}
              </Box>
            )}
            {membershipDisplayAccreditation?.label && (
              <MembershipTag
                label={membershipDisplayAccreditation.label}
                explainerId={membershipExplainerId}
                onOpenExplainer={setOpenAccreditationId}
              />
            )}
          </Box>
        )}

        {bioSnippet && (
          <Typography
            className={styles.bioText}
            variant="body2"
            data-testid="coach-card-bio"
          >
            {bioSnippet}
          </Typography>
        )}

        {isInfoVisualizationEnabled && (
          <Box className={styles.infoVisualization}>
            {_rankingInfo?.matchedGeoLocation?.distance != null && (
              <Typography variant="caption" display="block">
                  ~{Math.round(_rankingInfo.matchedGeoLocation.distance / 1609.34)} miles away
              </Typography>
            )}
            {hasPlusSubscription && <Typography variant="caption" display="block">★ Plus Subscription</Typography>}
            <Typography variant="caption" display="block">
                Random Int: {randomInt}
            </Typography>
          </Box>
        )}
      </Box>

      {profileHref ? (
        <Link
          href={profileHref}
          className={styles.cardOverlayLink}
          aria-label={profileAriaLabel}
          data-gtm-coach-search
          data-testid="coach-card-profile-link"
        >
          <span className={styles.srOnly} aria-hidden="true">{profileAriaLabel}</span>
        </Link>
      ) : null}

      {showIntroVideoBadge && (
        <Dialog
          open={isIntroVideoOpen}
          onClose={handleIntroVideoDialogClose}
          maxWidth="md"
          fullWidth
          aria-labelledby="coach-card-intro-video-dialog-title"
          classes={{ paper: styles.introVideoDialogPaper }}
        >
          <h2 id="coach-card-intro-video-dialog-title" className={styles.srOnly}>
            {`Introduction video for ${coachName}`}
          </h2>
          <IconButton
            aria-label="Close intro video"
            onClick={handleIntroVideoDialogClose}
            className={styles.introVideoDialogClose}
          >
            <IconClose />
          </IconButton>
          <DialogContent className={styles.introVideoDialogContent}>
            {isIntroVideoOpen && videoId && (
              <Embed
                data-gtm-element-name="coach-intro-video"
                data-gtm-coach-bookable={bookable}
                data-gtm-coach-location={placeLabel}
                data-gtm-coach-facility={facilityName}
                data-gtm-coach-id={universalId}
                data-gtm-coach-profile-id={objectID}
                data-gtm-profile-coach={coachName}
                data-gtm-intro-video-url={effectiveIntroductionVideoUrl}
                provider="youtube"
                id={videoId}
                autoPlay
              />
            )}
            {isIntroVideoOpen && !videoId && isFetchingIntroVideo && (
              <Box className={styles.introVideoDialogLoading} aria-live="polite">
                <CircularProgress color="inherit" size={40} />
              </Box>
            )}
            {isIntroVideoOpen && !videoId && !isFetchingIntroVideo && introVideoFetchFailed && (
              <Box className={styles.introVideoDialogLoading}>
                <Typography variant="body1" component="p" className={styles.introVideoDialogError}>
                    This intro video is unavailable right now.
                </Typography>
              </Box>
            )}
          </DialogContent>
        </Dialog>
      )}
      <CoachAccreditationExplainerDialog
        accreditationId={openAccreditationId}
        onClose={handleMembershipExplainerClose}
      />
    </Card>
  )
}

CoachCard.propTypes = {
  coachInfo: PropTypes.object,
  isStory: PropTypes.bool,
  hasIntroVideo: PropTypes.bool,
  source: PropTypes.string,
  isSaved: PropTypes.bool,
  onToggleSaved: PropTypes.func,
  hideSaveToggle: PropTypes.bool,
  bookThisWeek: PropTypes.bool,
  layout: PropTypes.oneOf(['grid', 'embedded']),
  /** Show this facility's name and city/state (facility pages) instead of the nearest/primary one. */
  externalFacilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Role line under the name (academy staff, e.g. "Director of Instruction"). */
  title: PropTypes.string,
}

export default CoachCard
